import React, {useEffect, useMemo, useState} from "react";
import moment from "moment";
import {useTranslation} from "react-i18next";
import {Button, Pagination, Spin, Tooltip} from "antd";
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    EyeOutlined,
    FileTextOutlined,
    UploadOutlined
} from "@ant-design/icons";
import {RiSparklingFill} from "react-icons/ri";
import {useFetch} from "../../../../../hooks/useFetch";
import {useStore} from "../../../../../hooks/useStore";
import {
    ActionDate,
    ActionReason,
    ActionTitle,
    ActionUser,
    EmptyState,
    HistoryContentColumn,
    HistoryEntry,
    HistoryIconColumn,
    HistoryList,
    HistoryMetaColumn,
    ModalTitleWrapper,
    PaginationWrapper,
    StyledModal
} from "./DocumentHistoryModal.styled";

const PAGE_SIZE = 4;

const ACTION_CONFIG = {
    uploaded: {
        color: "#1890ff",
        icon: UploadOutlined
    },
    approved: {
        color: "#52c41a",
        icon: CheckCircleOutlined
    },
    rejected: {
        color: "#ff4d4f",
        icon: CloseCircleOutlined
    }
};

export const DocumentHistoryModal = ({documentName, visible, onClose, courierId, documentType}) => {
    const {t} = useTranslation(["courierDetail"]);
    const stores = useStore();
    const [page, setPage] = useState(1);

    useEffect(() => {
        if (visible) {
            setPage(1);
        }
    }, [visible, documentType]);

    const skip = (page - 1) * PAGE_SIZE;

    const [historyLoading, historyResult] = useFetch({
        defaultDataValue: {history: [], count: 0},
        fetchFn: stores.courierStore.getCourierDocumentsHistory.bind(null, {
            courierId,
            documentType,
            skip,
            limit: PAGE_SIZE
        }),
        dependencies: [courierId, documentType, skip, visible],
        initialCall: Boolean(courierId) && Boolean(documentType),
        condition: Boolean(courierId) && Boolean(documentType) && visible
    });

    const historyItems = historyResult?.history || [];
    const totalCount = historyResult?.count || 0;

    const paginationLabel = useMemo(() => {
        if (totalCount === 0) {
            return "0 / 0";
        }

        const start = skip + 1;
        const end = Math.min(skip + PAGE_SIZE, totalCount);
        return `${start}-${end} / ${totalCount}`;
    }, [skip, totalCount]);

    const renderActionIcon = action => {
        const config = ACTION_CONFIG[action] || ACTION_CONFIG.uploaded;
        const IconComponent = config.icon;

        return <IconComponent style={{fontSize: 16, color: config.color}} />;
    };

    const getDocumentUrl = record => record?.file?.publicUrl || record?.document?.publicUrl;

    const modalTitle = (
        <ModalTitleWrapper>
            <FileTextOutlined style={{color: "#1890ff", fontSize: 18}} />
            {t("documentHistory.titleWithDocument", {documentName})}
        </ModalTitleWrapper>
    );

    return (
        <StyledModal
            title={modalTitle}
            visible={visible}
            onCancel={onClose}
            okText="Kapat"
            onOk={onClose}
            width={640}
            cancelButtonProps={{style: {display: "none"}}}
            destroyOnClose
        >
            <Spin spinning={historyLoading}>
                {historyItems.length === 0 && !historyLoading ? (
                    <EmptyState>{t("documentHistory.emptyText")}</EmptyState>
                ) : (
                    <HistoryList>
                        {historyItems.map(record => {
                            const actionConfig =
                                ACTION_CONFIG[record.action] || ACTION_CONFIG.uploaded;
                            const documentUrl = getDocumentUrl(record);

                            return (
                                <HistoryEntry key={record._id}>
                                    <HistoryIconColumn>
                                        {renderActionIcon(record.action)}
                                    </HistoryIconColumn>
                                    <HistoryContentColumn>
                                        <ActionTitle $color={actionConfig.color}>
                                            {t(`documentHistory.actions.${record.action}`, {
                                                defaultValue: record.action
                                            })}
                                        </ActionTitle>
                                        <ActionUser>
                                            <Tooltip
                                                title={
                                                    record?.processedByAutomation
                                                        ? t("documentHistory.automationTooltip")
                                                        : record?.actionBy?.fullName
                                                }
                                            >
                                                <span>
                                                    {record?.actionBy?.fullName || "-"}
                                                    {record?.processedByAutomation ? (
                                                        <RiSparklingFill style={{marginLeft: 4}} />
                                                    ) : null}
                                                </span>
                                            </Tooltip>
                                        </ActionUser>
                                        {record.action === "rejected" && record.reason ? (
                                            <ActionReason>{record.reason}</ActionReason>
                                        ) : null}
                                    </HistoryContentColumn>
                                    <HistoryMetaColumn>
                                        <ActionDate>
                                            {record.createdAt
                                                ? moment(record.createdAt).format(
                                                      "DD.MM.YYYY HH:mm"
                                                  )
                                                : "-"}
                                        </ActionDate>
                                        {documentUrl ? (
                                            <Button
                                                size="small"
                                                icon={<EyeOutlined />}
                                                onClick={() => window.open(documentUrl, "_blank")}
                                            >
                                                {t("documentHistory.viewDocument")}
                                            </Button>
                                        ) : null}
                                    </HistoryMetaColumn>
                                </HistoryEntry>
                            );
                        })}
                    </HistoryList>
                )}
            </Spin>
            {totalCount > PAGE_SIZE ? (
                <PaginationWrapper>
                    <Pagination
                        total={totalCount}
                        current={page}
                        pageSize={PAGE_SIZE}
                        showSizeChanger={false}
                        hideOnSinglePage
                        onChange={setPage}
                        showTotal={false}
                    />
                </PaginationWrapper>
            ) : null}
        </StyledModal>
    );
};
